import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { aiApi } from "@/api/aiApi.ts";
import type { SearchDocument } from "@/types";
import PdfImage from "@/assets/search/pdf.svg?react";
import MdImage from "@/assets/search/ic-MarkDown.svg?react";

import "./index.css";
import { getFileStream } from "@/api/client.ts";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible.tsx";
import { PdfView } from "@/components/pdfView";
import { PdfViewH5 } from "@/components/pdfViewH5";
import TypewriterMarkdown from "@/components/TypewriterMarkdown";
import { HoverTip } from "@/components/hoverTip";
import { useTranslation } from "@/locales";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Undo2 } from "lucide-react";

interface MarkdownData {
  title: string;
  content: string;
}

interface SpecialPageParams {
  fileId: string;
  citationIndex: string;
  page: string;
  conversationNo: string;
  type: string;
}

export default function SpecialPage() {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useState<SpecialPageParams>({
    citationIndex: "",
    conversationNo: "",
    fileId: "",
    page: "",
    type: "",
  });
  const [knowledge, setKnowledge] = useState<SearchDocument[]>([]);
  const [useKnowledge, setUseKnowledge] = useState<SearchDocument[]>([]);
  const [pdfStream, setPdfStream] = useState<null | string>();
  const [record, setRecord] = useState<SearchDocument>({});
  const [markDown, setMarkDown] = useState<MarkdownData | null>({
    title: "",
    content: "",
  });
  useEffect(() => {
    // 获取查询字符串
    const queryString = location.search;

    // 解析参数
    const urlParams = new URLSearchParams(queryString);
    const paramsObj: SpecialPageParams = {
      fileId: "",
      citationIndex: "",
      page: "",
      conversationNo: "",
      type: "",
    };

    // 转换为对象
    urlParams.forEach((value, key) => {
      if (key === "fileId") {
        paramsObj.fileId = value;
      } else if (key === "citationIndex") {
        paramsObj.citationIndex = value;
      } else if (key === "page") {
        paramsObj.page = value;
      } else if (key === "conversationNo") {
        paramsObj.conversationNo = value;
      } else if (key === "type") {
        paramsObj.type = value;
      }
    });
    setParams(paramsObj);

    console.log("params", paramsObj);
    if (paramsObj.conversationNo) {
      fetchKnowledge(paramsObj.conversationNo, paramsObj.fileId);
    }
  }, []);

  useEffect(() => {
    console.log("params", params);
    if (params.type == "PDF") {
      if (params.fileId && params.citationIndex && params.page) {
        getFileStream(`/v1/knowledge/file/pdf/content`, {
          fileId: params.fileId,
        }).then((blob) => {
          console.log(blob);
          const objectUrl = URL.createObjectURL(blob);
          setPdfStream(objectUrl);
          console.log(objectUrl);
        });
      }
    } else {
      if (params.fileId) {
        aiApi.conversation
          .getMarkdownFile(params.fileId)
          .then((res) => {
            setMarkDown(res as MarkdownData);
          })
          .catch((err) => {
            console.log(err);
          });
      }
    }
  }, [params]);

  const fetchKnowledge = async (conversationNo: string, fileId: string) => {
    try {
      const response = (await aiApi.conversation.getKnowledge(conversationNo)) as SearchDocument[];
      if (response) {
        // 根据 use 字段分组
        const usedItems = response.filter((item) => item.used === true);
        const unusedItems = response.filter((item) => !item.used);

        setUseKnowledge(usedItems);
        setKnowledge(unusedItems);

        // 设置当前文件名
        response.forEach((item) => {
          if (item.fileId == Number(fileId)) {
            setRecord(item);
            if (item.fileName) document.title = item.fileName;
          }
        });
      }
    } catch (error) {
      console.error("获取知识库失败:", error);
    }
  };

  return (
    <div className={"special-page bg-[var(--background-left_navigation_web)] h-[100vh] flex"}>
      {!isMobile && (
        <div className={"left-container min-w-[306px] w-[306px] bg-[var(--background-left_navigation_web)] pl-[8px]"}>
          <div className={"left-labels-container"}>
            <Collapsible defaultOpen={true}>
              <CollapsibleTrigger>
                <div className={"mt-[15px] mb-[5px]"}>
                  {t("chat.referencedFiles")}
                  {useKnowledge.length ? `(${useKnowledge.length})` : ""}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="h-[calc(100vh-50px)] overflow-y-auto">
                {useKnowledge.map((item, kInd) => (
                  <div className={"label-item"} key={item.fileId}>
                    <div
                      className={item.fileId == Number(params.fileId) ? "left-citation-content active" : "left-citation-content "}
                      onClick={() => {
                        setParams({
                          ...params,
                          fileId: item.fileId.toString(),
                          page: "1",
                          type: item.type,
                        });
                        setRecord(item);
                        if (item.fileName) document.title = item.fileName;
                      }}
                    >
                      <div className="flex items-start mb-[4px]">
                        <div className={"flex text-[14px]"}>{item.type == "PDF" ? <PdfImage className="w-[16px] h-[16px] mr-1" /> : <MdImage className="w-[16px] h-[16px] mr-1" />}</div>

                        <div className="text-[14px] text-[var(--text-text_primary)] font-medium truncate-1-lines">{`${item?.fileName}`}</div>
                      </div>
                      <div className="flex items-center gap-1 overflow-hidden">
                        {item && item?.labels
                          ? item?.labels.map((label, index) => {
                            return (
                              <HoverTip key={`label-${label.key}-${item?.fileId}-${Math.random()}`} content={label.value || ""} position="bottom">
                                <div className="inline-flex items-center text-center px-[8px] py-[4px] bg-[#FAFAFA] hover:bg-gray-300 border border-[#E6E6E6] rounded-md text-[11px] leading-[11px] text-gray-700 transition-colors cursor-pointer max-w-[90px] min-w-0 justify-center dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-300">
                                  <span className="truncate">{label.value || ""}</span>
                                </div>
                              </HoverTip>
                            );
                          })
                          : null}
                      </div>
                      {/*<div className="flex justify-between text-[10px] text-[#8A8A8A] mt-2">*/}
                      {/*    <div >收錄於 {item?.knowledgeName || ''}</div>*/}
                      {/*    <div>{item?.inclusionDate || ''}</div>*/}
                      {/*</div>*/}
                    </div>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      )}
      <div className={"right-container flex-1 flex flex-col"}>
        <div className="pdf-container bg-[var(--background-page_background_web)] flex-1 h-full overflow-hidden">
          {params.type == "PDF" &&
            pdfStream &&
            (isMobile ? (
              <PdfViewH5 file={pdfStream} initialPage={Number(params.page)} fileName={record?.fileName} />
            ) : (
              <PdfView file={pdfStream} initialPage={Number(params.page)} record={record} />
            ))}
          {params.type == "MD" && markDown && (
            <div className={"h-full flex-1 bg-[var(--background-page_background_web)]"}>
              <div className="header" style={{ padding: "12px 12px" }}>
                <div className={"text-left  title text-[14px] font-medium text-[var(--s-color-text-primary)] h-[36px]"}>
                  <div className={"text-s-color-text-primary s-font-small-strong box-border w-[90%]"}>
                    {
                      isMobile ? (
                        <div className="flex items-center">
                          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                            <Undo2 className="h-6 w-6" />
                          </Button>
                          <div className="px-[4px] ml-2 truncate-1-lines  w-fit max-w-full flex items-center justify-start rounded-[4px] cursor-pointer select-none">
                            {markDown.title}
                          </div>
                        </div>

                      ) : (
                        <HoverTip content={markDown.title}>
                          <div className="px-[4px] truncate-1-lines  w-fit max-w-full flex items-center justify-start rounded-[4px] cursor-pointer select-none hover:u-hover-show-parent-CigIit">
                            {markDown.title}
                          </div>
                        </HoverTip>
                      )
                    }
                  </div>
                </div>
              </div>
              <div className={" h-full flex-1 overflow-y-auto document"}>
                <div className={"answer-content p-[12px]"}>
                  <TypewriterMarkdown content={markDown.content}></TypewriterMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
